import { Position } from '@interfaces/client-account/client-account-models.interface';
import { FilteredAccount } from '@interfaces/models/client-accounts.interface';
import { createSelector } from '@ngrx/store';
import {
  getAccountName,
  getFormattedBrokerageAccounts,
  hasOptionsHoldings,
  isOptionsAuthorizedAccount,
} from '@shared/helpers';
import { AccountTypes } from '@shared/models';
import { selectClientAccountsState } from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  selectCwpCashPlusSweepTransferToggle,
  selectCwpMultiItMoToggle,
  selectNonVCOrgTransferToggle,
  selectVCOrgToOrgToggle,
} from '@store/selectors/feature/feature.selectors';

import {
  AccountRejectionReasons,
  areAllPositionsMutualFunds,
  checkOrgToggleRejection,
  createFilteredAccount,
  FilteredClientAccount,
  getAccountNumber,
  getClientAccountsEntity,
  getServiceAgreement,
  hasVcAccountTransactAuthority,
  isSpecificAccountType,
  MarginCode,
  OptionPermission,
  RejectedAccount,
  RestrictionType,
} from '../../../reducers/client-accounts/client-accounts.reducer';
import { isCashPlusEligiblePosition } from '../multi-internal-transfer.helpers';
import * as fromMultiIT from '../multi-internal-transfer.selectors';
import {
  checkAccountType,
  checkNonBrokerage,
  checkNonBrokerageAndNonMutualFund,
  checkPermissionType,
  checkPrimaryOwner,
  checkRoleType,
  checkServiceAgreement,
  fromPositionsHasMoneyMarketFund,
  getCombinedAccountTypeSet,
  getEligibleAccounts,
} from './eligibility.helpers';

// Type definitions for selector return types

// Interface for selectors that return both eligible and rejected accounts
export interface EligibilityWithRejects {
  poid: string;
  eligibleAccounts: FilteredAccount[];
  rejectedAccounts: RejectedAccount[];
}

export interface MultiInternalTransferEligibilityResult {
  isInternalTransferEligible: boolean;
  fromAccounts?: FilteredAccount[];
  transferFromType?: AccountTypes;
  ineligibleFromAccountType?: AccountTypes;
  rejectedFromAccounts?: RejectedAccount[];
  rejectedToAccounts?: RejectedAccount[];
}

// Transfer To
export const selectMultiITToAccountsWithRejects = createSelector(
  selectClientAccountsState,
  fromMultiIT.selectMultiITTriageTransferTo,
  fromMultiIT.selectMultiITFrom,
  fromMultiIT.selectMultiITFromSelectedHoldings,
  selectVCOrgToOrgToggle,
  selectCwpMultiItMoToggle,
  selectCwpCashPlusSweepTransferToggle,
  selectNonVCOrgTransferToggle,
  fromMultiIT.selectMultiITTriageTransferFrom,
  (
    clientAccountState,
    transferToAccountTypes,
    transferFrom,
    selectedHoldings,
    vcOrgToOrgToggle,
    multiItMoEnabled,
    sweepTransferEnabled,
    nonVcOrgTransferToggle,
    transferFromAccountType,
  ): EligibilityWithRejects => {
    const rejectedAccounts: RejectedAccount[] = [];
    const poid = clientAccountState.entity?.vgClientPoid || '';

    const transferToSpecificAccountTypes = transferToAccountTypes.filter(isSpecificAccountType);
    if (transferToSpecificAccountTypes.length === 0) {
      return { poid, eligibleAccounts: [], rejectedAccounts: [] };
    }

    const hasOptionHoldings = multiItMoEnabled && hasOptionsHoldings(selectedHoldings);

    const eligibleToAccounts: FilteredClientAccount = getEligibleAccounts(
      clientAccountState,
      transferToSpecificAccountTypes,
      true,
      rejectedAccounts,
    );

    const targetToAccountTypes = getCombinedAccountTypeSet(transferToSpecificAccountTypes);
    if (multiItMoEnabled) {
      // Reclassify cross-type M&O rejections before VC remap; no-op when toggle is off.
      rejectedAccounts.forEach((rejected) => {
        if (
          rejected.rejectionReason === AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT &&
          !targetToAccountTypes.has(rejected.accountType)
        ) {
          rejected.rejectionReason = AccountRejectionReasons.ACCOUNT_TYPE;
        }
      });
    }

    // When the VC toggle is on, remap generic ACCOUNT_TYPE rejections to VC-specific reasons
    // so the UI shows a meaningful message — mirrors single-IT getEligibleInternalTransferToAccounts.
    const fromIsVC = transferFrom?.selectedAccount?.accountData?.isVanguardCharitable;
    const hasAccountTypeRejections = rejectedAccounts.some(
      (r) => r.rejectionReason === AccountRejectionReasons.ACCOUNT_TYPE,
    );

    if (vcOrgToOrgToggle && hasAccountTypeRejections) {
      const accountsById = new Map(
        getClientAccountsEntity(clientAccountState)?.accounts?.map((a) => [a.accountId, a]),
      );
      rejectedAccounts
        .filter((r) => r.rejectionReason === AccountRejectionReasons.ACCOUNT_TYPE)
        .forEach((rejected) => {
          const isVC = accountsById.get(rejected.accountId)?.isVanguardCharitable;
          if (fromIsVC === false && isVC === true) {
            rejected.rejectionReason = AccountRejectionReasons.FROM_NON_VC_TO_VC_INELIGIBLE;
          } else if (fromIsVC === true && isVC !== true) {
            rejected.rejectionReason = AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE;
          }
        });
    }

    const fromSelectedAccount = transferFrom?.selectedAccount;
    const fromIsVanguardCharitable =
      fromSelectedAccount?.accountData?.isVanguardCharitable === true;

    const shouldFilterVCMatch = vcOrgToOrgToggle && fromSelectedAccount?.accountData != null;

    // Toggle-off ineligibility on the TO side — intentionally scoped to ORG→ORG.
    // We don't gate TO-side ORG accounts when FROM is non-ORG (e.g., INDV→ORG
    // stays open regardless of toggle state).
    const isOrgToOrgContext =
      transferFromAccountType === AccountTypes.ORG &&
      transferToSpecificAccountTypes.includes(AccountTypes.ORG);
    const shouldRejectVCOrgAccounts =
      isOrgToOrgContext && nonVcOrgTransferToggle && !vcOrgToOrgToggle;
    const shouldRejectNonVCOrgAccounts =
      isOrgToOrgContext && vcOrgToOrgToggle && !nonVcOrgTransferToggle;

    const shouldFilterCashPlus = !fromPositionsHasMoneyMarketFund(
      transferFrom,
      sweepTransferEnabled,
    );
    // Filter out accounts that have ACTR restriction if the transfer includes non-money market funds
    const shouldFilterACTR = !areAllPositionsMutualFunds(selectedHoldings);

    // Filter out the from account
    eligibleToAccounts.accounts = eligibleToAccounts.accounts.filter((account) => {
      if (account.accountId === fromSelectedAccount?.accountData.accountId) {
        return false;
      }

      // Toggle-off rejection — VC or non-VC path disabled by feature toggle.
      if (
        checkOrgToggleRejection(
          account,
          rejectedAccounts,
          shouldRejectNonVCOrgAccounts,
          shouldRejectVCOrgAccounts,
        )
      ) {
        return false;
      }

      if (shouldFilterVCMatch && account.isVanguardCharitable !== fromIsVanguardCharitable) {
        // FROM is VC → rejected account is non-VC (VC→non-VC path is invalid).
        // FROM is non-VC → rejected account is VC (non-VC→VC path is invalid).
        const rejectionReason = fromIsVanguardCharitable
          ? AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE
          : AccountRejectionReasons.FROM_NON_VC_TO_VC_INELIGIBLE;
        rejectedAccounts.push({
          ...account,
          formattedAccountName: getAccountName(account, false),
          rejectionReason: rejectionReason,
        });
        return false;
      }

      if (shouldFilterCashPlus && account.cashManagementAccount) {
        rejectedAccounts.push({
          accountId: account.accountId,
          accountName: account.accountName,
          accountType: account.accountType,
          formattedAccountName: getAccountName(
            {
              ...account,
              accountNumber: getAccountNumber({
                ...account,
                optionPermission: OptionPermission.NO_PAPERS,
                marginCode: MarginCode.CASH,
              }),
            },
            false,
          ),
          accountNumber: getAccountNumber({
            ...account,
            optionPermission: OptionPermission.NO_PAPERS,
            marginCode: MarginCode.CASH,
          }),
          rejectionReason: AccountRejectionReasons.NON_MONEY_MARKET_POSITION_SELECTED,
        });
        return false;
      }

      if (shouldFilterACTR) {
        const hasRestriction = account.restrictions.includes(RestrictionType.ACTR);
        if (hasRestriction) {
          rejectedAccounts.push({
            ...account,
            formattedAccountName: getAccountName(account, false),
            rejectionReason: AccountRejectionReasons.ACTR_RESTRICTION,
          });
          return false;
        }
      }

      return true;
    });

    if (multiItMoEnabled) {
      const entity = getClientAccountsEntity(clientAccountState);
      const targetAccountTypes = getCombinedAccountTypeSet(transferToSpecificAccountTypes);

      const optionEligibleAccountIds = new Set(
        (entity?.accounts ?? [])
          .filter(isOptionsAuthorizedAccount)
          .map((account) => account.accountId),
      );

      const rawAccountsById = new Map((entity?.accounts ?? []).map((a) => [a.accountId, a]));

      const rescuedAccounts: FilteredAccount[] = [];
      const rescuedIds = new Set<string>();

      rejectedAccounts.forEach((rejected) => {
        if (
          rejected.rejectionReason === AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT &&
          optionEligibleAccountIds.has(rejected.accountId)
        ) {
          // Never rescue the FROM account into the TO list
          if (rejected.accountId === fromSelectedAccount?.accountData?.accountId) {
            return;
          }

          const rawAccount = rawAccountsById.get(rejected.accountId)!;
          const serviceAgreement = getServiceAgreement(
            rawAccount,
            entity?.serviceAgreements ?? null,
          );

          const passesRemainingChecks =
            checkNonBrokerage(rawAccount.productType, true) &&
            checkNonBrokerageAndNonMutualFund(rawAccount.productType, true) &&
            checkAccountType(rawAccount.accountType, targetAccountTypes) &&
            checkRoleType(rawAccount, poid, serviceAgreement) &&
            checkPermissionType(rawAccount, poid, serviceAgreement, true) &&
            checkServiceAgreement(serviceAgreement) &&
            checkPrimaryOwner(rawAccount, transferToSpecificAccountTypes, poid, serviceAgreement);

          if (passesRemainingChecks) {
            const filteredAccount = createFilteredAccount(rawAccount, serviceAgreement);
            if (filteredAccount) {
              rescuedAccounts.push(filteredAccount);
              rescuedIds.add(rawAccount.accountId);
            }
          }
        }
      });

      const remainingRejected = rejectedAccounts.filter(
        (r) =>
          !(
            r.rejectionReason === AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT &&
            rescuedIds.has(r.accountId)
          ),
      );

      let allEligible = [...eligibleToAccounts.accounts, ...rescuedAccounts];

      if (hasOptionHoldings) {
        const nonMoEligible = allEligible.filter(
          (account) => !optionEligibleAccountIds.has(account.accountId),
        );
        nonMoEligible.forEach((account) => {
          remainingRejected.push({
            accountId: account.accountId,
            accountName: account.accountName,
            accountType: account.accountType,
            formattedAccountName: getAccountName(account, false),
            accountNumber: account.accountNumber,
            ...(account.nickname && { nickname: account.nickname }),
            rejectionReason: AccountRejectionReasons.INELIGIBLE_FOR_OPTIONS_TRANSFER,
          });
        });

        allEligible = allEligible.filter((account) =>
          optionEligibleAccountIds.has(account.accountId),
        );
      }

      return {
        poid,
        eligibleAccounts: allEligible,
        rejectedAccounts: remainingRejected,
      };
    }

    return {
      poid,
      eligibleAccounts: eligibleToAccounts.accounts,
      rejectedAccounts,
    };
  },
);

//Transfer From
export const selectMultiITFromAccountsWithRejects = createSelector(
  selectClientAccountsState,
  fromMultiIT.selectMultiITTriageTransferFrom,
  fromMultiIT.selectMultiITTriageTransferTo,
  selectVCOrgToOrgToggle,
  selectCwpMultiItMoToggle,
  selectCwpCashPlusSweepTransferToggle,
  selectNonVCOrgTransferToggle,
  (
    clientAccountState,
    transferFromAccountType,
    transferToAccountTypes,
    vcOrgToOrgToggle,
    cwpMultiItMoToggle,
    sweepTransferEnabled,
    nonVcOrgTransferToggle,
  ): EligibilityWithRejects => {
    const rejectedAccounts: RejectedAccount[] = [];
    const poid = clientAccountState.entity?.vgClientPoid || '';

    const filteredAccounts = getEligibleAccounts(
      clientAccountState,
      [transferFromAccountType].filter(isSpecificAccountType),
      false,
      rejectedAccounts,
    );

    const targetFromAccountTypes = getCombinedAccountTypeSet(
      [transferFromAccountType].filter(isSpecificAccountType),
    );
    if (cwpMultiItMoToggle) {
      // Reclassify cross-type M&O rejections; no-op when toggle is off.
      rejectedAccounts.forEach((rejected) => {
        if (
          rejected.rejectionReason === AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT &&
          !targetFromAccountTypes.has(rejected.accountType)
        ) {
          rejected.rejectionReason = AccountRejectionReasons.ACCOUNT_TYPE;
        }
      });
    }

    // VC accounts are only eligible in FROM when TO also includes ORG — hide them otherwise.
    const isOrgToOrgTransfer =
      transferFromAccountType === AccountTypes.ORG &&
      (transferToAccountTypes?.includes(AccountTypes.ORG) ?? false);

    // Toggle-off ineligibility on the FROM side.
    // - VC toggle gates VC→VC only (ORG→ORG)
    // - NON_VC toggle gates every transfer where FROM is a non-VC ORG account
    //   (any TO type). When it's off, non-VC ORG accounts aren't transferable
    const isOrgFrom = transferFromAccountType === AccountTypes.ORG;
    const isOrgToOrgContext =
      isOrgFrom && (transferToAccountTypes?.includes(AccountTypes.ORG) ?? false);
    const shouldRejectVCOrgAccounts =
      isOrgToOrgContext && nonVcOrgTransferToggle && !vcOrgToOrgToggle;
    const shouldRejectNonVCOrgAccounts = isOrgFrom && vcOrgToOrgToggle && !nonVcOrgTransferToggle;

    filteredAccounts.accounts = filteredAccounts.accounts.filter((account) => {
      // Toggle-off rejection — VC or non-VC path disabled by feature toggle.
      if (
        checkOrgToggleRejection(
          account,
          rejectedAccounts,
          shouldRejectNonVCOrgAccounts,
          shouldRejectVCOrgAccounts,
        )
      ) {
        return false;
      }

      // VC account being rejected because the transfer isn't ORG-to-ORG —
      // FROM is VC and destination is non-VC.
      if (!isOrgToOrgTransfer && account.isVanguardCharitable === true) {
        rejectedAccounts.push({
          ...account,
          formattedAccountName: getAccountName(account, false),
          rejectionReason: AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE,
        });
        return false;
      }

      if (
        isOrgToOrgTransfer &&
        account.isVanguardCharitable === true &&
        !hasVcAccountTransactAuthority(poid, account.registeredRoles, account.grantedRoles)
      ) {
        rejectedAccounts.push({
          ...account,
          formattedAccountName: getAccountName(account, false),
          rejectionReason: AccountRejectionReasons.NO_VC_TRANSACT_AUTHORITY,
        });
        return false;
      }

      if (account.cashManagementAccount) {
        // If sweep transfer is on, keep Cash Plus sweep
        const filteredPositions: Position[] = sweepTransferEnabled
          ? account.positions
          : account.positions.filter((position) => !position.settlementFund);

        // check if there is at least one eligible holding
        const hasMoneyMarketFund: boolean = filteredPositions.some((position) =>
          isCashPlusEligiblePosition(position, sweepTransferEnabled),
        );

        if (hasMoneyMarketFund) {
          // Update account positions and include the account
          account.positions = filteredPositions;
          return true;
        } else {
          // exclude the account and push to rejectedAccounts
          rejectedAccounts.push({
            accountId: account.accountId,
            accountName: account.accountName,
            accountType: account.accountType,
            formattedAccountName: getAccountName(
              {
                ...account,
                accountNumber: getAccountNumber({
                  ...account,
                  optionPermission: OptionPermission.NO_PAPERS,
                  marginCode: MarginCode.CASH,
                }),
              },
              false,
            ),
            accountNumber: getAccountNumber({
              ...account,
              optionPermission: OptionPermission.NO_PAPERS,
              marginCode: MarginCode.CASH,
            }),
            rejectionReason: AccountRejectionReasons.ZERO_BALANCE,
          });
          return false;
        }
      }
      //include non-cash management accounts without changes
      return true;
    });

    if (cwpMultiItMoToggle) {
      const entity = getClientAccountsEntity(clientAccountState);
      const rawAccountsById = new Map((entity?.accounts ?? []).map((a) => [a.accountId, a]));

      const promotedAccounts: FilteredAccount[] = [];
      const remainingRejected: RejectedAccount[] = [];

      for (const rejected of rejectedAccounts) {
        const isMarginRejection =
          rejected.rejectionReason === AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT;

        if (isMarginRejection) {
          const rawAccount = rawAccountsById.get(rejected.accountId);
          if (
            rawAccount &&
            isOptionsAuthorizedAccount(rawAccount) &&
            checkAccountType(rawAccount.accountType, targetFromAccountTypes)
          ) {
            const serviceAgreement = getServiceAgreement(
              rawAccount,
              entity?.serviceAgreements ?? null,
            );
            const filteredAccount = createFilteredAccount(rawAccount, serviceAgreement);
            if (filteredAccount) {
              promotedAccounts.push(filteredAccount);
              continue;
            }
          }
        }
        remainingRejected.push(rejected);
      }

      return {
        poid,
        eligibleAccounts: [...filteredAccounts.accounts, ...promotedAccounts],
        rejectedAccounts: remainingRejected,
      };
    }

    return {
      poid,
      eligibleAccounts: filteredAccounts.accounts,
      rejectedAccounts,
    };
  },
);

export const selectMultiITToUnselectedEligibleAccounts = createSelector(
  selectMultiITToAccountsWithRejects,
  fromMultiIT.selectAllMultiITToSelectedAccountIds,
  (toAccountsWithRejects, alreadySelectedAccountIds): FilteredAccount[] => {
    let eligibleAccounts = toAccountsWithRejects.eligibleAccounts;
    const selectedAccountIdSet = new Set(alreadySelectedAccountIds);
    eligibleAccounts = eligibleAccounts.filter((account) => {
      return !selectedAccountIdSet.has(account.accountId);
    });
    return eligibleAccounts;
  },
);

export const selectMultiITHasNoOptionsEligibleToAccount = createSelector(
  fromMultiIT.selectMultiITFromSelectedHoldings,
  selectMultiITToAccountsWithRejects,
  selectCwpMultiItMoToggle,
  (selectedHoldings, toAccountsWithRejects, multiItMoEnabled): boolean => {
    if (!multiItMoEnabled) return false;
    if (!hasOptionsHoldings(selectedHoldings)) return false;
    return toAccountsWithRejects.eligibleAccounts.length === 0;
  },
);

export const selectMultiITHasOptionHoldings = createSelector(
  fromMultiIT.selectMultiITFromSelectedHoldings,
  selectCwpMultiItMoToggle,
  (selectedHoldings, multiItMoEnabled): boolean => {
    if (!multiItMoEnabled) return false;
    return hasOptionsHoldings(selectedHoldings);
  },
);

//Eligibility

export const selectMultiInternalTransferEligibility = createSelector(
  selectMultiITFromAccountsWithRejects,
  selectMultiITToAccountsWithRejects,
  fromMultiIT.selectMultiITTriageTransferFrom,
  (fromResult, toResult, transferFromType): MultiInternalTransferEligibilityResult => {
    // Extract data from selector results
    const eligibleFromAccounts = fromResult.eligibleAccounts;
    const rejectedFromAccounts = fromResult.rejectedAccounts;
    const rejectedToAccounts = toResult.rejectedAccounts;

    const hasEligibleFromAccounts: boolean =
      getFormattedBrokerageAccounts(eligibleFromAccounts).length >= 1;

    // Determine eligibility - assuming To accounts are always eligible
    const isInternalTransferEligible: boolean = hasEligibleFromAccounts;

    if (isInternalTransferEligible) {
      return {
        isInternalTransferEligible: true,
        fromAccounts: eligibleFromAccounts,
        transferFromType,
        rejectedFromAccounts,
        rejectedToAccounts,
      };
    } else {
      return {
        isInternalTransferEligible: false,
        ineligibleFromAccountType: transferFromType,
        rejectedFromAccounts,
        rejectedToAccounts,
      };
    }
  },
);
